import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import FilterBtn from "../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import CustomLoadingOverlay from "../../../Components/CustomLoadingOverlay";
import { createFilterHandler } from "../../../Utils/FilterUtils";
import httpclient from "../../../Utils";

import { getColumns } from "./columns";
import Filter from "./Filter";
import { useRegionalFreight } from "./useRegionalFreight";
import ConfirmationDialog from "./ConfirmationDialog";

const INITIAL_FILTER_STATE = {
  freightCode: "",
};

const fieldMappings = {
  freightCode: ["freight_code", "$eq"],
};

export default function RegionalFreight() {
  const [selectedFreight, setSelectedFreight] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
  const [openDeleteFreight, setOpenDeleteFreight] = useState(false);
  const [isDeletingFreight, setIsDeletingFreight] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([]);

  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const {
    regionalFreights,
    setRegionalFreights,
    loading,
    setLoading,
    rowCount,
    refresh,
    refreshRegionalFreights,
    allowAdd
  } = useRegionalFreight({
    autoFetch: true,
    pagination: {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    sorting: sortModel,
  });

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setRegionalFreights,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `/remote-bay-freight-code`,
    fieldMappings,
    refresh,
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    newFilterData[field] = "";

    delete newSubmittedData[field];
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(INITIAL_FILTER_STATE);
  };

  const handleAddFreight = () => {
    navigate("/regional-freight/add");
  };

  const handleDeleteFreight = async () => {
    setIsDeletingFreight(true);

    const API_URL = `/remote-bay-freight-code/delete/${selectedFreight.id}`;

    try {
      const { data } = await httpclient.post(API_URL);
      if (data.status === 200) {
        showNotification("success", data.message);
        setSelectedFreight("");
        setOpenDeleteFreight(false);
        refreshRegionalFreights(true);
      } else {
        showNotification("info", data.message);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setTimeout(() => {
          setOpenDeleteFreight(false);
        }, 1000);
      } else if (error.response?.status === 401) {
        refresh();
      }
      handleApiError(error);
    } finally {
      setIsDeletingFreight(false);
    }
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = getColumns(setSelectedFreight, setOpenDeleteFreight);

  const deleteFreightMessage = (
    <>
      Are you sure you want to delete freight code{" "}
      <strong>{selectedFreight?.freight_code}</strong> for bay{" "}
      <strong>{selectedFreight?.bay}</strong>?
    </>
  );

  return (
    <>
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 16px",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Regional Freight
            </Typography>

           

           
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
               {allowAdd.allow_add === 1 && (
              <Button
                variant="contained"
                size="large"
                sx={{
                  borderRadius: "4px",
                  marginRight: "16px",
                  backgroundColor: "#7152F3",
                  "&:hover": {
                    backgroundColor: "#5c34fa",
                  },
                }}
                startIcon={<AddIcon sx={{ marginRight: 0 }} />}
                onClick={handleAddFreight}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                  }}
                >
                  Add New Freight
                </Typography>
              </Button>
               )}
              <img
                src={filterOpen ? FilterBtnUp : FilterBtn}
                alt="Filter"
                style={{ cursor: "pointer" }}
                onClick={() => setFilterOpen(!filterOpen)}
              />
            </Box>
            
          </Box>

          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
          />

          <DataGrid
            rows={regionalFreights || []}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            slots={{
              loadingOverlay: CustomLoadingOverlay,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#48505E",
              },
            }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
          />
        </Box>
      </Box>
      {openDeleteFreight && (
        <ConfirmationDialog
          open={openDeleteFreight}
          handleClose={() => setOpenDeleteFreight(false)}
          title={"Delete Freight"}
          message={deleteFreightMessage}
          onConfirm={handleDeleteFreight}
          loading={isDeletingFreight}
        />
      )}
    </>
  );
}
